import   React              ,
       { useMemo             }  from 'react';

import   classNames            from 'classnames';
import   'flag-icons/css/flag-icons.min.css';

import   styles                from './UserRow.module.scss';

import   Icon                  from '../../widget/images/Icon';

import { User                }  from '../../../dataTypes/userManagment/types';
import { getInitials         ,
         generateInitialsAvatarDataUrl ,
         formatDateOrDash              }  from '../../../dataTypes/userManagment/utils';
import { COUNTRY_LIST         }  from '../../../dataTypes/adminManagment/countries';

// Statut du compte : rond coloré + libellé de la même couleur
const USER_STATUS_INFO: Record<string, { label: string; className: string }> = {
  Discover : { label: 'Découverte' , className: styles.status_Discover },
  Standard : { label: 'Standard'   , className: styles.status_Standard },
  Premium  : { label: 'Premium'    , className: styles.status_Premium  },
};

interface UserRowProps {
  user         : User;
  index        : number;
  onEdit       : (index: number) => void;
  onToggleLock : (user: User) => void;
  onDelete     : (user: User) => void;
}

export const UserRow = ({ user         ,
                          index        ,
                          onEdit       ,
                          onToggleLock ,
                          onDelete     }: UserRowProps) => {

  // Pas de photo pour les utilisateurs : même avatar généré (rond bleu + initiales) que dans AdminRow.
  const defaultPhotoImage = useMemo(
    () => generateInitialsAvatarDataUrl(getInitials(user.firstname, user.lastname, user.email)),
    [user.firstname, user.lastname, user.email]
  );

  // Champ historiquement vide pour les comptes créés avant l'ajout du pays — UserSheet(V2)
  // applique la même valeur par défaut à l'édition (initialUser.country === "" ? "France" : ...).
  const countryValue = user.country === '' ? 'France' : user.country;
  const countryCode  = COUNTRY_LIST.find((country) => country.name === countryValue)?.code;

  const statusInfo = USER_STATUS_INFO[user.status] ?? USER_STATUS_INFO.Discover;

  return (

    <div className={classNames(styles.row)}>

      <div className={classNames(styles.column_user, styles.standard)}>
        {
         defaultPhotoImage !== ''
         ? <img src       = {defaultPhotoImage}
                alt       = {`${user.firstname} ${user.lastname}`}
                className = {styles.thumbnail}
                onClick   = {() => onEdit(index)} />
         : <div className={styles.thumbnailPlaceholder} onClick={() => onEdit(index)}>
             <Icon name       = "user"
                   color      = "var(--color-Icon-FG)"
                   hoverColor = "var(--color-Icon-hover-FG)"
                   width      = {28}
                   height     = {28}
                   hoverable  = {false}
                   clickable  = {false} />
           </div>
        }
        <div className={classNames(styles.userInfo)}>
          <span className={classNames(styles.userName)}>
            {`${user.firstname} ${user.lastname}`.trim() || '—'}
          </span>
          <span className={classNames(styles.userEmail)}>{user.email || '—'}</span>
        </div>
      </div>

      <div className={classNames(styles.column_status, styles.standard, statusInfo.className)}>
        <span className={classNames(styles.statusDot)} />
        <span className={classNames(styles.statusLabel)}>{statusInfo.label}</span>
      </div>

      <div className={classNames(styles.column_locked, styles.standard)}>
        {
         user.locked
         ? (
          <div className={classNames(styles.statusBadge, styles.statusBadge_locked)}>
            <Icon name="lockedPadlock" color="#FFFFFF" hoverable={false} clickable={false} width={16} height={16} />
          </div>
         )
         : (
          <div className={classNames(styles.statusBadge, styles.statusBadge_unlocked)}>
            <Icon name="openPadlock" color="#FFFFFF" hoverable={false} clickable={false} width={16} height={16} />
          </div>
         )
        }
      </div>

      <div className={classNames(styles.column_location, styles.standard)}>
        {
         countryCode || user.city
         ? (
          <span className={classNames(styles.locationCell)}>
            {countryCode && <span className={classNames('fi', `fi-${countryCode}`, styles.flag)} />}
            <span>{user.city || '—'}</span>
          </span>
         )
         : '—'
        }
      </div>

      <div className={classNames(styles.column_creationDate, styles.standard)}>
        {formatDateOrDash(user.creationDate)}
      </div>

      <div className={classNames(styles.column_lastLogin, styles.standard)}>
        {formatDateOrDash(user.lastLoginDate)}
      </div>

      <div className={classNames(styles.column_action, styles.standard)}>
        <div className={styles.iconButton} onClick={() => onEdit(index)}>
          <Icon name       = "pen"
                color      = "var(--color-Icon-FG)"
                hoverColor = "var(--color-Icon-hover-FG)"
                width      = {20}
                height     = {20} />
        </div>
        <div className={styles.iconButton} onClick={() => onToggleLock(user)}>
          <Icon name       = {user.locked ? 'openPadlock' : 'lockedPadlock'}
                color      = "var(--color-Icon-FG)"
                hoverColor = "var(--color-Icon-hover-FG)"
                width      = {20}
                height     = {20} />
        </div>
        <div className={styles.iconButton} onClick={() => onDelete(user)}>
          <Icon name       = "trashCan"
                color      = "var(--color-Icon-FG)"
                hoverColor = "var(--color-Icon-hover-FG)"
                width      = {20}
                height     = {20} />
        </div>
      </div>

    </div>
  );
};

export default UserRow;
